// libs/utils/logger/src/lib/logger.ts
// Optional integrations are loaded at runtime if present.
import { loggerConfig } from './config/loggerConfig.js';
import { logError, flushSentry } from './sentry.js';
const isNode = typeof process !== 'undefined' && typeof process.versions?.node !== 'undefined';
let nodeRequire;
if (isNode) {
    try {
        // In CJS bundles, require is available; in other runtimes it may not be.
        nodeRequire = require;
    }
    catch {
        nodeRequire = undefined;
    }
}
let sanitizeContext = (ctx) => ctx;
try {
    if (nodeRequire) {
        sanitizeContext = nodeRequire('@digiwedge/data-utils-sanitize-context').sanitizeContext;
    }
}
catch {
    // optional dependency not installed
    void 0;
}
const pino = (() => {
    const hasWindow = typeof globalThis.window !== 'undefined';
    const noop = () => undefined;
    if (!hasWindow) {
        // Node.js path: real pino function with optional .destination
        if (nodeRequire) {
            return nodeRequire('pino');
        }
        // Fallback to noop logger when require is unavailable (e.g. ESM tests)
        return (() => ({
            fatal: noop,
            error: noop,
            warn: noop,
            info: noop,
            debug: noop,
            trace: noop,
        }));
    }
    const dummyDestination = (() => ({
        write: () => true,
        // Add any other required properties/methods for DestinationStream if needed
    }));
    const factory = Object.assign(
    // Browser path: callable function returning a pino-like logger
    (() => ({
        fatal: noop,
        error: noop,
        warn: noop,
        info: noop,
        debug: noop,
        trace: noop,
    })), { destination: dummyDestination });
    return factory;
})();
// Try to resolve an ECS formatter function if the package exists.
function resolveEcsFormat() {
    try {
        const modA = nodeRequire?.('@elastic/ecs-pino-format');
        const maybeFnA = modA?.default ?? modA;
        if (typeof maybeFnA === 'function')
            return maybeFnA;
    }
    catch {
        void 0;
    }
    try {
        const modB = nodeRequire?.('pino-ecs-format');
        const maybeFnB = modB?.default ?? modB;
        if (typeof maybeFnB === 'function')
            return maybeFnB;
    }
    catch {
        void 0;
    }
    return null;
}
function resolvePrettyTransport() {
    try {
        // Probe presence; if not installed, let pino use defaults
        nodeRequire?.('pino-pretty');
        return {
            target: 'pino-pretty',
            options: { colorize: true, translateTime: true, ignore: 'pid,hostname' },
        };
    }
    catch {
        return undefined;
    }
}
function createElasticStream() {
    if (!isNode || !loggerConfig.elasticsearch.enabled)
        return null;
    try {
        const mod = nodeRequire?.('pino-elasticsearch');
        if (!mod)
            return null;
        const factory = typeof mod.default === 'function'
            ? mod.default
            : mod;
        return factory({
            index: `${loggerConfig.elasticsearch.indexPrefix}-${new Date().toISOString().split('T')[0]}`,
            node: loggerConfig.elasticsearch.node,
        });
    }
    catch {
        return null; // elastic disabled silently if dep missing
    }
}
/* -------------------------------------------------------------------------- */
export class Logger {
    static ERROR_KEY = 'error';
    elasticStream = createElasticStream();
    log;
    constructor() {
        const dest = this.elasticStream ??
            (typeof pino.destination === 'function' ? pino.destination() : undefined);
        this.log = pino(this.buildOptions(), dest);
    }
    /* ----------------------------- options builder -------------------------- */
    buildOptions() {
        if (isNode && process.env['NODE_ENV'] === 'development') {
            return {
                level: loggerConfig.level,
                transport: resolvePrettyTransport(),
            };
        }
        const ecs = resolveEcsFormat();
        if (ecs)
            return ecs();
        return { level: loggerConfig.level };
    }
    /* --------------------------- helpers & guards --------------------------- */
    static serialiseError(err) {
        return err instanceof Error
            ? { message: err.message, name: err.name, stack: err.stack }
            : { error: err };
    }
    static sanitise(meta) {
        return sanitizeContext(meta, {
            redactionText: '[REDACTED]',
            logRedactedKeys: true,
            maxDepth: 5,
        });
    }
    /* ------------------------------ public API ------------------------------ */
    logAt(level, message, meta = {}) {
        const safeMeta = Logger.sanitise(meta);
        const metaWithError = Logger.ERROR_KEY in safeMeta
            ? {
                ...safeMeta,
                error: Logger.serialiseError(safeMeta[Logger.ERROR_KEY]),
            }
            : safeMeta;
        this.log[level](metaWithError, message);
        if (level === 'error' && safeMeta[Logger.ERROR_KEY] instanceof Error) {
            logError(safeMeta[Logger.ERROR_KEY], safeMeta);
        }
    }
    debug(msg, meta) {
        this.logAt('debug', msg, meta);
    }
    info(msg, meta) {
        this.logAt('info', msg, meta);
    }
    warn(msg, meta) {
        this.logAt('warn', msg, meta);
    }
    error(msg, meta) {
        this.logAt('error', msg, meta);
    }
    fatal(msg, meta) {
        this.logAt('fatal', msg, meta);
    }
    async flush() {
        if (this.elasticStream?.flush)
            await this.elasticStream.flush();
        await flushSentry();
    }
}
/** Re-usable singleton */
export const logger = new Logger();
//# sourceMappingURL=logger.js.map